/**
 * Keeps the AZA widget's idle (pre-connected) websocket fresh so that the
 * Transitive Auth (TA) token attached to it never expires before use.
 *
 * Why: the TA token is minted by our CloudFront edge lambda on the websocket
 * upgrade request and AZA stores it for the life of that connection. The AZA
 * widget pre-opens a socket and lets it sit idle for up to 10 minutes before a
 * question is sent on it, but the TA token only lives 5 minutes. A question sent
 * on a socket that waited more than 5 minutes reaches Clarity Assist with an
 * EXPIRED_TOKEN and is rejected for TA-enforced users.
 *
 * What: when the user interacts with the page (pointerdown / keydown / focusin)
 * and the AZA socket is idle (nothing sent yet) and at least
 * {@link IDLE_SOCKET_MAX_AGE_MS} old, close it. The widget auto-reconnects, the
 * reconnect passes through the edge lambda, and a fresh token is minted before
 * the user can send. Refresh is driven by user activity rather than a timer, so
 * an untouched tab generates no extra reconnects or token mints.
 *
 * A socket that has sent anything is never closed, so an in-flight question or
 * streaming reply is never interrupted. Typed-but-unsent text lives in the input
 * box, not on the socket, so it is unaffected. If the socket is closed in the
 * same gesture that sends (pointerdown precedes click), the widget sees it is no
 * longer OPEN and opens a fresh socket on demand.
 *
 * Safety: no token is read or handled here; only an idle socket is closed.
 */

export const AZA_WEBSOCKET_PATH = '/apis/AtoZAssistantWebsocket';
// TA token TTL is 5 min; refresh with a margin.
export const IDLE_SOCKET_MAX_AGE_MS = 4 * 60 * 1000;
export const IDLE_SOCKET_CLOSE_REASON = 'Idle AZA socket refresh';
export const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'focusin'] as const;

const INSTALLED = Symbol.for('clarity.azaSocketFreshness.installed');

type MarkedWebSocketCtor = typeof WebSocket & { [INSTALLED]?: true };

export function isAzaWebSocketUrl(url: string | URL): boolean {
  try {
    const parsed = new URL(String(url), window.location.href);
    return (
      parsed.host === window.location.host &&
      parsed.pathname.startsWith(AZA_WEBSOCKET_PATH)
    );
  } catch {
    return false;
  }
}

/**
 * Installs the idle-socket refresh by replacing window.WebSocket with a
 * transparent subclass and registering passive activity listeners. Idempotent.
 * Must run before the AZA widget opens its first socket; sockets created
 * earlier are not affected.
 */
export function installAzaSocketFreshness(
  maxAgeMs: number = IDLE_SOCKET_MAX_AGE_MS,
): void {
  if (typeof window === 'undefined' || typeof window.WebSocket !== 'function') {
    return;
  }
  const NativeWebSocket = window.WebSocket as MarkedWebSocketCtor;
  if (NativeWebSocket[INSTALLED]) {
    return;
  }

  // Open AZA sockets that have not sent anything yet, with their open time.
  const idleSockets = new Map<WebSocket, number>();

  const refreshStaleIdleSockets = (): void => {
    const now = Date.now();
    idleSockets.forEach((openedAt, socket) => {
      if (
        now - openedAt >= maxAgeMs &&
        socket.readyState === NativeWebSocket.OPEN
      ) {
        idleSockets.delete(socket);
        socket.close(1000, IDLE_SOCKET_CLOSE_REASON);
      }
    });
  };

  class FreshAzaWebSocket extends NativeWebSocket {
    constructor(url: string | URL, protocols?: string | string[]) {
      super(url, protocols);
      if (!isAzaWebSocketUrl(url)) {
        return;
      }
      // addEventListener (not onopen/onclose) so the widget's own property
      // handlers, which it reassigns when handing the socket off, are untouched.
      this.addEventListener('open', () => idleSockets.set(this, Date.now()));
      this.addEventListener('close', () => idleSockets.delete(this));
    }

    send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
      idleSockets.delete(this);
      super.send(data);
    }
  }

  ACTIVITY_EVENTS.forEach((type) =>
    document.addEventListener(type, refreshStaleIdleSockets, {
      capture: true,
      passive: true,
    }),
  );

  Object.defineProperty(FreshAzaWebSocket, INSTALLED, { value: true });
  window.WebSocket = FreshAzaWebSocket;
}
